// web/src/registry/featureCopy/_part-2.ts —— FEATURE_COPY 的第 2/2 段（128 个元素）
// 拆自 web/src/registry/featureCopy.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { FeatureCopy } from '../featureCopy';

export const FEATURE_COPY_PART_2: Record<string, FeatureCopy> = {

  'ai.prompts': {
    desc: '喂给大模型的模板注册表：可改、可停、可回退',
    keywords: ['提示词', 'prompt', '模板', '提示词管理', '话术模板'],
  },
  'ai.usage': {
    desc: '每一次大模型调用的流水账：谁触发、问什么答什么、花了多少',
    keywords: ['调用监控', 'LLM 调用', '调用流水', 'token', '用量', '调用记录'],
  },
  'ai.cost': {
    desc: '成本单价、异常告警与配置完整性检查',
    keywords: ['成本', '费用', '单价', '计费', '成本告警', '成本核算'],
  },
  'ai.studio': {
    desc: '调提示词与大模型效果的实验台',
    keywords: ['调试台', 'LLM 调试', '提示词调试', '实验台', '智能调试'],
  },
  // ---------- 通信能力 ----------
  'telephony.gateways': {
    desc: '出局大门：外呼与点击呼叫从哪个网关出去、走哪个运营商',
    keywords: ['网关', 'SIP 网关', '中继网关', '出局', '运营商', '落地'],
  },
  'telephony.trunks': {
    desc: '反向注册到本系统 FreeSWITCH 的运营商中继账号',
    keywords: ['中继', '中继账号', '注册账号', '线路', 'FXO'],
  },
  'telephony.audio': {
    desc: '通用音频素材库与场景绑定（等待音、AI 故障提示音）',
    keywords: ['放音', '音频', '提示音', '等待音乐', '语音文件', '素材库'],
    actions: ['上传音频', '试听'],
  },
  'telephony.settings': {
    desc: '通信侧设置入口：SIP 参数与呼叫媒体',
    keywords: ['通信设置', 'SIP 设置', '媒体设置', '平台通信'],
  },
  'telephony.routing': {
    desc: '进线接线的三步配置同页：号码/主叫/时段走哪条路由、按键菜单接到哪、上下班时段怎么分流',
    keywords: ['呼叫路由', '进线路由', '接线表', '呼入路由', 'IVR', '时段', 'IVR 菜单', '接线配置'],
    actions: ['新建路由', '新建 IVR 菜单', '新建时段'],
  },
  'telephony.sms': {
    desc: '系统对外短信的下发留痕：验证码、回访与催缴提醒、外呼挂机短信',
    keywords: ['短信', '短信记录', '短信下发', '短信日志', '短信发送', '验证码留痕'],
  },
  // ---------- 流程与数据采集 ----------
  'workflow.flows': {
    desc: '把一次客户诉求的处理过程画成流程图并发布给机器人执行',
    keywords: ['流程', '业务流程', '流程编排', '画流程', '流程图', '流程设计'],
  },
  'workflow.approvals': {
    desc: '审批流编排器：谁审、几个人审、驳回回到哪',
    keywords: ['审批流', '审批流程', '工单审核', '流程审批', '审核流程'],
  },
  'data-collection.fields': {
    desc: '共享字段词表：电话里要收的号、流程动作要核验的身份信息',
    keywords: ['字段', '字段定义', '信息字段', '词表', '采集字段'],
  },
  'data-collection.rules': {
    desc: '让 AI 边听边填表：从对话里抽订单号、金额、地址等结构化信息',
    keywords: ['信息采集', '自动填表', '采集规则', '信息抽取', '实体抽取', '自动信息采集'],
  },
  // ---------- 用户与组织 ----------
  'identity.users': {
    desc: '账号与登录：谁能登录、密码、启用状态',
    keywords: ['用户', '账号', '用户管理', '登录账号', '账号管理'],
    actions: ['新建用户', '重置密码', '停用账号'],
  },
  'identity.agents': {
    desc: '坐席分机档案：工号、分机、软电话注册密码与技能组绑定',
    keywords: ['坐席', '坐席管理', '分机', '工号', '软电话账号'],
  },
  'identity.orgs': {
    desc: '组织树与数据权限范围（能看到谁的数据）',
    keywords: ['组织', '组织架构', '部门', '机构', '组织树', '数据权限'],
  },
  'identity.roles': {
    desc: '角色与权限点分配：谁能看到、能操作什么',
    keywords: ['角色', '权限', '权限分配', '角色管理', '授权'],
  },
  // ---------- 集成 ----------
  // 「开放平台」已整体下线（应用/密钥管理页与接口一并摘除），其条目与「应用管理」页签语料随之下线。
  'integration.data-sources': {
    desc: '把企业已有业务系统（如 BOSS）的查询接口登记成查询动作，供机器人在通话里调用',
    keywords: ['数据源', '外部接口', 'BOSS', '查询动作', '接口登记', '外部数据'],
  },
  // ---------- 安全与合规 ----------
  'compliance.settings': {
    desc: '数据保留期、脱敏等合规参数',
    keywords: ['合规', '数据合规', '保留期', '脱敏', '隐私'],
  },
  'compliance.security': {
    desc: '登录安全：二次验证、密码策略、IP 白名单',
    keywords: ['安全', '2FA', '二次验证', '密码策略', '登录安全', '安全认证'],
  },
  'compliance.egress': {
    desc: '送往云上 AI 的文本留痕：发给了哪个平台、发的是什么',
    keywords: ['数据出域', '出域日志', '外发', '云上 AI', '留痕'],
  },
  // ---------- 审计与诊断 ----------
  'audit.logs': {
    desc: '谁在什么时候对哪个对象做了什么、结果如何、从哪个 IP',
    keywords: ['审计', '操作日志', '审计日志', '留证', '追溯'],
  },
  'audit.ai': {
    desc: 'AI 调用的审计视图（与调用监控同源）',
    keywords: ['AI 审计', 'AI 调用日志', '模型审计'],
  },
  'diagnostics.diag': {
    desc: '客服报障的第一落点：输入「哪一通电话」，一次拿到全链路时间线',
    keywords: ['诊断', '故障诊断', '排障', '全链路', '时间线', '定位问题'],
    actions: ['按通话号诊断', '查 FS 通道'],
  },

  // ---------- 公告中心 ----------
  'notice.manage': {
    desc: '发布公告并配置单选/填空题，坐席进入管理台后强制阅读作答、全部答对才放行；可设开始与失效时间',
    keywords: ['公告', '公告管理', '通知公告', '必读', '考试', '答题', '题库', '过关'],
    actions: ['新建公告', '配置单选/填空题', '发布公告', '设置开始与失效时间'],
  },

  // ---------- 被某页收成页内 Tab 的功能（侧栏无独立入口，但用户会直接找） ----------
  'identity.agents#agents': {
    desc: '坐席分机档案：工号、分机、软电话注册密码与所属技能组',
    keywords: ['坐席', '坐席档案', '坐席管理', '分机', '工号', '软电话账号'],
  },
  'identity.agents#skill-groups': {
    desc: '坐席的业务分组与排队队列本身：转人工排到哪个组、组里怎么接、排队超时怎么办',
    keywords: ['技能组', '队列', '分组', '排队组', '坐席组', '坐席分组'],
  },
  'telephony.routing#routes': {
    desc: '总机接线表：什么号、什么主叫、什么时段走哪条目的地',
    keywords: ['路由', '呼叫路由', '接线表', 'DID', '进线号码', '目的地', '呼入路由'],
  },
  'telephony.routing#ivr': {
    desc: '按键语音导航（「咨询请按 1」）的菜单地图编辑器：按键 → 去向（队列/分机/子菜单）',
    keywords: ['IVR', 'IVR 菜单', '语音导航', '按键菜单', '自助语音', '话务菜单', '语音菜单'],
  },
  'quality.sampling#samples': {
    desc: '把人工复核结论变成 AI 的教学材料（回流样本）',
    keywords: ['样本回流', '回流', '训练样本', '复核结论'],
  },
  'ticketing.settings#templates': {
    desc: '建单快捷键：选一个模板就带出类型、优先级、审核流程与字段默认值',
    keywords: ['建单模板', '工单模板', '模板', '快捷建单'],
  },
  'ticketing.settings#routing': {
    desc: '按字段自动派给谁：工单自动分配的规则表',
    keywords: ['派单', '派单规则', '自动分派', '分单', '工单派单'],
  },
  'ticketing.settings#auto-create': {
    desc: 'AI 在通话/会话里自动生成工单的开关与判定条件',
    keywords: ['自动建单', 'AI 建单', '自动开单', '工单自动化'],
  },
  'customer-insights.settings#satisfaction': {
    desc: '全渠道满意度收集的唯一配置点：开关、分数标签、话术、有效期',
    keywords: ['满意度设置', '评价设置', '评分设置', '满意度开关'],
  },
  'customer-insights.settings#voc-labels': {
    desc: 'VOC 打标用的标签体系维护',
    keywords: ['VOC 标签', '标签管理', '打标标签', '客户标签', '标签体系'],
  },
  'telephony.settings#sip': {
    desc: 'FreeSWITCH 两套 SIP profile 的注册放行开关与坐席软电话全局注册密码',
    keywords: ['SIP 参数', 'SIP 设置', '注册开关', '盲注册', '注册密码', '注册鉴权'],
  },
  'telephony.settings#media': {
    desc: 'FreeSWITCH 域、媒体服务地址与 zitch 软电话信令地址等呼叫侧基础设施参数',
    keywords: ['媒体', '媒体地址', 'FS 域', 'FreeSWITCH 域', '呼叫参数', '呼叫与媒体', 'zitch 信令', '软电话信令'],
  },
  'telephony.settings#whitelist': {
    desc: '试运行期只允许名单内号码呼入/呼出，配置直接下发交换引擎、绕过 bcc 也无法拨打或接听名单外号码',
    keywords: ['电话白名单', '外呼白名单', '白名单', '号码白名单', '防冒用', '试运行', '限制外呼', '限制呼入', '呼出白名单', '呼入白名单'],
  },

  // ---------- 其余页内 Tab：说明为必填（回答「这页签是干什么的」），别名只给「用户会另找名字」的那些 ----------
  // 待办中心三页签（原「消息中心」两页并入后，key 由状态改成页签）：说明必填
  'workflow.inbox#todo': { desc: '等我处理的流程任务（工单审批等）：状态可在页内下拉切换，默认全部', keywords: ['待办任务', '待我处理', '审批', '我的待办'] },
  'workflow.inbox#notice': { desc: '我收到的站内通知（含已读）：按阅读状态与类型筛选', keywords: ['通知记录', '已读通知', '系统通知'] },
  'workflow.inbox#announce': { desc: '我完成过或已失效的公告：完成时间、答题次数与公告正文', keywords: ['公告历史', '已完成公告', '失效公告'] },
  'bot.intents#vocab': { desc: '意图词表本体：每个叶子意图绑一个动作', keywords: ['意图词表', '词表', '意图维护'] },
  'bot.intents#ruleword': {
    desc: '行为判定词表：哪些话算问候/告别/否定/风险/承诺度，供语音与坐席辅助链路零 LLM 快判',
    keywords: ['规则词表', '通用规则词表', '行为判定词表', '判定词表', '风险词', '承诺度', '否定词', '告别词'],
  },
  'bot.intents#stats': { desc: '意图命中与分布统计' },
  'bot.intents#test': { desc: '输入一句话看会被识别成哪个意图' },
  'bot.intents#shadow': {
    desc: '影子档位下同一句话的 v1/v2 两条判定对照：一致率与分歧样本',
    keywords: ['意图对照', 'A/B', '识别引擎对比', '分歧样本'],
  },
  'quality.sampling#reports': { desc: '质检报告列表：每通话单的质检得分、违规点与复核改分', keywords: ['质检报告', '报告', '质检结果', '得分明细'] },
  'quality.sampling#appeals': { desc: '坐席对质检结论的申诉与复核裁定', keywords: ['申诉', '质检申诉', '复核裁定'] },
  'quality.sampling#flow-reports': {
    desc: '流程质检：按工单走完整流程评质量（不是评一通电话）：环节时效、流程合规与客户反馈',
    keywords: ['流程质检', '全流程质检', '工单质检'],
  },
  'quality.rules#rules': {
    desc: '质检规则本体：跨集合总览全部规则，可直接新建或改类型/判定条件/命中得分/适用维度',
    keywords: ['规则', '质检规则', '判定条件', '命中得分', '违规级别', '红线'],
  },
  'quality.rules#rule-sets': {
    desc: '规则集：按组织归拢的一组质检规则；左侧组织树选层，右侧列出该层的集合并可进「查看明细」逐条改 0–5 分评分标准（用途只分任务/实时）',
    keywords: ['规则集', '规则集合', '质检规则集', '组织规则集', '查看明细', '评分标准'],
  },
  // 知识库只剩 WeKnora 三个入口（知识问答 / 知识管理 / 问答历史），且都是 iframe 壳、页内无本系统页签：
  // 原「知识待办 / 知识分析 / 知识库设置 / 召回验证」四页的页签语料已随页面与页签登记（navTabs.ts）一并删除。
  'ticketing.settings#fields': { desc: '工单表单的自定义字段设计器', keywords: ['工单字段', '自定义字段', '字段设计'] },
  'ticketing.settings#approval': { desc: '审批流编排：谁审、几人审、驳回回到哪', keywords: ['审核流程', '审批流', '工单审核'] },
  'operations.cockpit#cockpit': { desc: '驾驶舱本体：指标异常标红与根因清单' },
  'operations.cockpit#agents': { desc: '坐席实时状态（与运营监控 · 坐席状态同源）', keywords: ['坐席状态', '坐席实况', '坐席监控'] },
  'operations.cockpit#queues': { desc: '排队与话务实况（与运营监控 · 队列实况同源）', keywords: ['队列实况', '排队情况', '排队实况'] },
  'operations.settings#alerts': { desc: '驾驶舱「什么算异常」的阈值中枢', keywords: ['预警设置', '预警阈值', '驾驶舱预警'] },
  'operations.settings#agents': { desc: '坐席上线状态与小休原因的统一设置', keywords: ['坐席运营', '小休原因', '坐席状态设置'] },
  'operations.shifts#calendar': { desc: '排班日历：谁哪天什么班', keywords: ['排班日历', '排班表', '值班日历'] },
  'operations.shifts#templates': { desc: '班次模板：几点到几点算一个班', keywords: ['班次模板', '班次', '班次设置'] },
  'analytics.report.core#summary': { desc: '统计汇总：总量指标与趋势' },
  'analytics.report.core#agents': { desc: '每个坐席接了多少、说了多久', keywords: ['坐席工作量', '坐席报表', '坐席产能'] },
  'analytics.report.core#queues': { desc: '按技能组/队列看话务量与服务水平', keywords: ['队列报表', '技能组报表', '队列统计'] },
  'analytics.report.core#satisfaction': { desc: '评价均分与星级分布' },
  'analytics.report.ai#ai': { desc: 'AI 接待量与转人工情况' },
  'analytics.report.ai#aiEfficiency': { desc: 'AI 分流漏斗、LLM 账单与产能等价', keywords: ['AI 效能', 'AI 成本', '省了多少人力'] },
  'analytics.report.ai#aiObservability': { desc: 'AI 链路各阶段时延分位与供应商健康度', keywords: ['AI 可观测', '时延', '链路监控'] },
  'analytics.report.ai#chatBots': { desc: '每个在线客服机器人的解决率与转人工率' },
  'analytics.report.service#sla': { desc: '20 秒应答率、呼损率与平均等待', keywords: ['SLA', '应答率', '服务水平'] },
  'analytics.report.service#agentHours': { desc: '坐席在线工时与占用情况', keywords: ['坐席工时', '工时统计', '工时'] },
  'analytics.report.service#outbound': { desc: '外呼接通率与效果指标', keywords: ['外呼效果', '外呼报表', '外呼统计'] },
  'ai.providers#asr': { desc: '语音识别（ASR）引擎绑定与密钥', keywords: ['ASR', '语音识别', '录音转写模型'] },
  'ai.providers#tts': { desc: '语音合成（TTS）引擎绑定与音色', keywords: ['TTS', '语音合成', '音色'] },
  'ai.providers#llm': { desc: '大模型（LLM）引擎绑定与参数', keywords: ['LLM', '大模型', '语言模型'] },
  'ai.providers#embedding': { desc: '向量化（Embedding）引擎绑定，知识库检索用', keywords: ['Embedding', '向量化', '向量模型'] },
  'ai.providers#fileasr': { desc: '录音文件离线识别配置（通话录音整段转写，质检输入）', keywords: ['录音文件识别', '离线转写', '录音识别'] },
  'ai.usage#board': {
    desc: 'AI 调用量看板：ASR/TTS/LLM 等能力的调用数、成功率、时延、音频时长与成本，按分钟/小时/天/周/月切换',
    keywords: ['AI 调用统计', '调用看板', 'AI 用量', 'ASR 调用量', 'TTS 调用量', 'LLM 用量', '调用量趋势'],
  },
  'ai.usage#calls': { desc: '逐次 LLM 调用明细：内容、token、耗时与失败原因', keywords: ['LLM 调用历史', '调用明细', 'LLM 日志'] },
  'ai.cost#cost': { desc: '各能力的分单价配置，用于算成本', keywords: ['单价', '计费标准', '费率'] },
  'ai.cost#alert': { desc: '成本异常告警规则', keywords: ['异常告警', '成本告警', '成本异常'] },
  'ai.cost#configcheck': { desc: '检查各 AI 能力配置是否完整可用', keywords: ['配置完整性', '配置检查', '配置体检'] },
  'integration.data-sources#sources': { desc: '外部数据源（如 BOSS）的连接登记', keywords: ['数据源', '外部接口', '接口登记'] },
  'integration.data-sources#calls': { desc: '外部接口的调用记录与结果', keywords: ['调用记录', '外部调用日志', '外部调用'] },
  'diagnostics.diag#timeline': { desc: '一通电话从进线到挂断的全链路时间线', keywords: ['全链路', '时间线', '链路追踪'] },
  'diagnostics.diag#fs': { desc: 'FreeSWITCH 通道快照与全部通道变量', keywords: ['FS 通道', '通道变量', 'uuid'] },
  'diagnostics.diag#agent': { desc: '坐席侧的注册与状态诊断', keywords: ['坐席状态', '分机注册', '分机状态'] },
  'diagnostics.diag#queue': { desc: '排队与派单环节诊断', keywords: ['队列', '派单诊断', '排队'] },
  'diagnostics.diag#transcript': { desc: '这通电话的转写文本与识别问题', keywords: ['转写', '识别文本', 'ASR 结果'] },
  'diagnostics.diag#ailogs': { desc: '这通电话里的 AI 调用日志', keywords: ['AI 日志', '模型调用日志', '模型日志'] },
  'diagnostics.diag#quality': { desc: '这通电话的质检结果与违规点' },
  'diagnostics.diag#fsmeta': { desc: 'FreeSWITCH 运行概览与原始输出', keywords: ['FS 概览', 'FS 状态', 'FS 运行概览'] },
  // ---------- 独立设置/子页（侧栏无入口、但路由确实挂载：用户能点到，也得能搜到） ----------
  'outbound.settings': { desc: '外呼运行参数：同时执行的外呼任务数（全局 AI 并发已归「语音行为默认」）', keywords: ['外呼设置', '外呼配置', '外呼参数', '任务并发'] },
  'outbound.settings.concurrency': { desc: '外呼调度参数：同时执行的外呼任务数（全局 AI 并发见「语音行为默认」）', keywords: ['外呼并发', '并发数', '任务并发', '调度参数'] },
  'outbound.board': { desc: '号码看板：外呼号码的分布与状态总览', keywords: ['号码看板', '号码总览', '外呼看板'] },
  'chat.settings': { desc: '在线渠道接待参数：在线接待量与电话优先于在线会话', keywords: ['在线客服设置', '在线设置', '接待量', '在线接待', '电话优先'] },
  'chat.analytics': { desc: '在线客服的会话量、响应与转化分析', keywords: ['在线客服分析', '在线分析', '会话分析'] },
  'quality.settings': { desc: '质检与录音：会话质检采样比例与内线互拨录音白名单', keywords: ['质检设置', '录音设置', '质检与录音设置', '抽检比例', '互拨录音'] },
  'quality.appeals': { desc: '坐席看自己质检结果与申诉的地方', keywords: ['我的质检', '我的申诉', '质检申诉', '我的质检与申诉'] },
  'ticketing.settings.fields': { desc: '工单表单自定义字段设计器 + 按字段自动派单', keywords: ['工单字段', '字段与派单', '自定义字段', '字段设计'] },
  'ticketing.settings.approval': { desc: '工单审批流编排：谁审、几人审、驳回回到哪', keywords: ['工单审核流程', '工单审批', '审核流程'] },
  'operations.settings.alerts': { desc: '驾驶舱「什么算异常」的阈值中枢', keywords: ['驾驶舱预警设置', '预警阈值', '预警设置'] },
  'operations.settings.agents': { desc: '坐席上线状态与小休原因的统一设置面', keywords: ['坐席运营设置', '小休原因', '坐席状态设置'] },};
